/**
 * CreateMetaAdWizard — A4.3.3.
 *
 * Shared wizard frame that pivots on the `creativeSource` prop the
 * CreativeLibraryPicker hands in. When `mode === 'new'` the Creative
 * step renders the variant-specific authoring UI (Link in this PR;
 * Video / Carousel land in A4.3.3.1). When `mode === 'existing'` the
 * Creative step is skipped entirely and Advantage+ Creative renders as
 * a panel at the top of Review.
 *
 * Chrome mirrors `CreateAdSetWizard`: ProgressIndicator, ARIA dialog,
 * footer error banner above the button row, ConfirmDialog on
 * cancel-with-dirty-form, responsive desktop/mobile footer layout.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useResetScrollOnStepChange } from '@/hooks/useResetScrollOnStepChange';

import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useMaterializePendingTargeted } from '@/hooks/useMaterializePendingTargeted';
import { useMetaAdSetsQuery } from '@/hooks/queries/useMetaCampaignsQuery';
import { batchDeleteImages } from '@/services/api/media';
import { linkifyUrls } from '@/utils/linkify';
import {
  metaAdsApi,
  type CreateMetaAdRequest,
  type MetaAdCreateStatus,
  type MetaAdCreative,
  type MetaAdvantagePlusCreativeSpec,
  type MetaCreativeSource,
  type MetaLinkCreativeSpec,
  type MetaPage,
} from '@/services/api/metaAds';
import { useAuthStore } from '@/store/authStore';
import type { PendingTargeted } from '@/types/media';
import type { Campaign } from '@/types/campaign';
import type { AppConfiguration } from '@/types/api';
import { debug } from '@/utils/debug';
import { appendOptimisticMetaAd } from '@/utils/optimisticMetaCache';

import { ProgressIndicator } from '../CampaignCreation/ProgressIndicator';
import { LINK_CHAR_LIMITS } from './constants/metaAdMatrix';
import { AdBasicsStep } from './steps/ad/AdBasicsStep';
import { AdReviewStep } from './steps/ad/AdReviewStep';
import { AdSetPickerStep } from './steps/ad/AdSetPickerStep';
import { LinkCreativeStep } from './steps/ad/LinkCreativeStep';

// eslint-disable-next-line no-control-regex
const NAME_PATTERN = /^[^\x00-\x1f\x7f]+$/;

export interface CreateMetaAdWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  campaignId: string;
  parentCampaign: Campaign;
  /** Optional pre-pick. Skip the AdSetPicker step when set. */
  adSetId?: string;
  /** Required. Carries the picker's choice (existing creative_id or
   *  new + variant). The wizard does not launch standalone. */
  creativeSource: MetaCreativeSource;
  /** When `creativeSource.mode === 'existing'`, the picker's preview
   *  payload for the Review step. */
  pickedCreative?: MetaAdCreative | null;
}

interface FormErrors {
  name?: string;
  pageId?: string;
  instagramActorId?: string;
  adSetId?: string;
  image?: string;
  headline?: string;
  primary_text?: string;
  description?: string;
  link_url?: string;
  lead_gen_form_id?: string;
}

const ALL_WIZARD_STEPS = [
  { label: 'Basics', description: '' },
  { label: 'Ad Set', description: '' },
  { label: 'Creative', description: '' },
  { label: 'Review', description: '' },
];

const defaultLinkSpec = (): MetaLinkCreativeSpec => ({
  creative_type: 'link',
  image_url: '',
  headline: '',
  primary_text: '',
  description: undefined,
  call_to_action: 'LEARN_MORE',
  link_url: '',
  lead_gen_form_id: undefined,
});

const defaultAdvantagePlus = (): MetaAdvantagePlusCreativeSpec => ({
  enabled: false,
  text_variations: true,
  image_variations: true,
});

export const CreateMetaAdWizard: React.FC<CreateMetaAdWizardProps> = ({
  isOpen,
  onClose,
  onSuccess,
  campaignId,
  parentCampaign,
  adSetId: adSetIdProp,
  creativeSource,
  pickedCreative = null,
}) => {
  // Form state.
  const [adName, setAdName] = useState('');
  const [status, setStatus] = useState<MetaAdCreateStatus>('PAUSED');
  const [pageId, setPageId] = useState('');
  const [instagramActorId, setInstagramActorId] = useState('');
  const [selectedAdSetId, setSelectedAdSetId] = useState(adSetIdProp ?? '');
  const [linkSpec, setLinkSpec] = useState<MetaLinkCreativeSpec>(defaultLinkSpec);
  /** PendingTargeted from TargetedImagePicker. Materialized to a GCS
   *  signed URL at submit time and uploaded to Meta's adimages
   *  endpoint server-side. `null` until the user picks. */
  const [pendingImage, setPendingImage] = useState<PendingTargeted | null>(
    null,
  );
  const [advantagePlusCreative, setAdvantagePlusCreative] =
    useState<MetaAdvantagePlusCreativeSpec>(defaultAdvantagePlus);
  const [availablePages, setAvailablePages] = useState<MetaPage[]>([]);
  const [isLoadingPages, setIsLoadingPages] = useState(false);

  const materializePending = useMaterializePendingTargeted();
  const memberUuid = useAuthStore((s) => s.memberUuid);
  const queryClient = useQueryClient();

  // Step + UX state.
  const [currentStep, setCurrentStep] = useState(1);
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  useResetScrollOnStepChange(currentStep, scrollBodyRef);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [hasDirty, setHasDirty] = useState(false);

  const adAccountId = useMemo(() => {
    const meta = parentCampaign as Campaign & {
      meta_ad_account_id?: string;
    };
    return meta.meta_ad_account_id ?? '';
  }, [parentCampaign]);

  // LEADS-objective ads require a lead_gen_form_id on the creative and
  // must share the Page with the parent ad set. Detect once per open.
  const isLeadsObjective = useMemo(() => {
    const meta = parentCampaign as Campaign & { objective?: string | null };
    return meta.objective === 'OUTCOME_LEADS';
  }, [parentCampaign]);

  // Track the picked lead form's name so AdReviewStep can render it
  // without an extra fetch. LinkCreativeStep pushes this up.
  const [leadFormName, setLeadFormName] = useState<string | null>(null);

  // Fetch parent ad sets when objective is LEADS so we can inherit
  // `promoted_object.page_id` onto the ad creative. React Query caches
  // via key ['meta-ad-sets', campaignId] — same key used elsewhere so
  // opening the wizard is usually a cache hit. Gate on `isOpen` too —
  // the component returns null when closed but hooks still fire, so
  // without this the query would run in the background for every
  // LEADS-parent card render on the account page (Copilot round 8 on
  // PR #216).
  const {
    data: adSetsData,
    isPending: isAdSetsPending,
    isError: isAdSetsError,
  } = useMetaAdSetsQuery(
    isOpen && isLeadsObjective ? campaignId : '',
  );
  const selectedAdSet = useMemo(() => {
    if (!isLeadsObjective || !selectedAdSetId) return null;
    return (
      adSetsData?.ad_sets.find((as) => as.ad_set_id === selectedAdSetId) ??
      null
    );
  }, [isLeadsObjective, selectedAdSetId, adSetsData]);
  const inheritedPageId = useMemo(() => {
    const po = selectedAdSet?.promoted_object as
      | { page_id?: string | null }
      | null
      | undefined;
    return po?.page_id ?? null;
  }, [selectedAdSet]);
  const isPageInherited = Boolean(inheritedPageId);

  // Copilot round 2 on PR #216: Meta only accepts form-attached
  // creatives when the parent ad set's `destination_type` is `ON_AD`
  // (instant form). For other LEADS variants (MESSENGER,
  // INSTAGRAM_DIRECT, WEBSITE, PHONE_CALL) Meta rejects the create
  // with the "Creative with lead form can only be used for Lead
  // Generation objective and ON_AD destination" family of errors.
  // Gate the picker + validation on `destination_type === 'ON_AD'`
  // to avoid the confusing "required in wizard but rejected by Meta"
  // dead-end. When we can't resolve the destination_type (pre-
  // destination-type-persistence ad sets), default to showing the
  // picker — the ad set was auto-defaulted to ON_AD before this field
  // was persisted, and Meta will reject with a specific error if not.
  const adSetDestinationType = selectedAdSet?.destination_type ?? null;
  const isLeadsFormPickerApplicable =
    isLeadsObjective &&
    (adSetDestinationType === 'ON_AD' || adSetDestinationType === null);

  // LEADS parent, Basics step should suppress the Page dropdown and
  // wait for inheritance to resolve. True in two cases:
  //   (a) no ad set picked yet — step 2 will collect one.
  //   (b) ad set picked but the ad-sets query is still loading —
  //       inheritance may still materialize.
  // NOT true when the query has resolved and inheritedPageId is null.
  // That case falls through to the dropdown so the user can pick a
  // Page manually. This is the recovery path for the known ingest
  // gap documented at `meta_campaigns.py:2311-2323` — the daily Meta
  // ingest doesn't yet persist `promoted_object.page_id` /
  // `destination_type` on `MetaAdSetDataDaily`, so any LEADS ad set
  // older than the wizard's optimistic-write window has
  // `promoted_object=null` in the app_configuration snapshot. Without
  // this fallback, prod users see "This ad set has no Facebook Page
  // attached" on every ingest-stale LEADS ad set — even ones that
  // Meta actually has a Page attached to.
  //
  // Silent-overwrite prevention still holds: the inheritance useEffect
  // below only writes pageId when inheritedPageId is truthy, so a
  // user's dropdown pick isn't clobbered in the fallback branch.
  const isLeadsPendingAdSet =
    isLeadsObjective &&
    !isPageInherited &&
    (isAdSetsPending || !selectedAdSetId);

  // Force the wizard's pageId to the inherited value when present. For
  // LEADS parents with a resolvable inheritance, this owns pageId; the
  // Basics render branch shows read-only inherited. For LEADS parents
  // where inheritedPageId is null (ingest-stale ad set — see
  // isLeadsPendingAdSet doc above), this effect is a no-op and the
  // Basics-step Page dropdown owns the write. Including `pageId` in
  // deps is loop-safe because the `!==` guard makes the setter a
  // no-op on the fire that follows its own update (Copilot round 1 on
  // PR #216).
  useEffect(() => {
    if (inheritedPageId && inheritedPageId !== pageId) {
      setPageId(inheritedPageId);
    }
  }, [inheritedPageId, pageId]);

  // Lazy-load the page picker on first open.
  useEffect(() => {
    if (!isOpen || !adAccountId) return;
    let cancelled = false;
    setIsLoadingPages(true);
    metaAdsApi
      .listPages(adAccountId)
      .then((pages) => {
        if (!cancelled) {
          setAvailablePages(pages);
          // Auto-pick the first Page ONLY for non-LEADS parents. For
          // LEADS parents the Page is inherited from the ad set — the
          // inheritedPageId useEffect above owns that write. Letting
          // the auto-pick run for LEADS would seed pageId with an
          // arbitrary first-in-list Page, which the inherit effect
          // then overwrites — same silent-mutation shape we removed
          // from the Basics UI.
          if (!isLeadsObjective) {
            setPageId((current) => current || pages[0]?.id || '');
          }
        }
      })
      .catch((err) => {
        debug.log('CreateMetaAdWizard: listPages failed', err);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPages(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, adAccountId, isLeadsObjective]);

  // Reset state when the modal opens with a fresh creativeSource.
  useEffect(() => {
    if (!isOpen) return;
    setAdName('');
    setStatus('PAUSED');
    setInstagramActorId('');
    setSelectedAdSetId(adSetIdProp ?? '');
    setLinkSpec(defaultLinkSpec());
    setPendingImage(null);
    setAdvantagePlusCreative(defaultAdvantagePlus());
    setCurrentStep(1);
    setErrors({});
    setSubmitError(null);
    setShowCancelConfirm(false);
    setHasDirty(false);
    // Reset pageId explicitly — the lazy-load effect's auto-select
    // short-circuits when the current value is truthy, so leaving the
    // last session's pick in state would silently keep a stale Page
    // selected when the wizard reopens against a different ad account
    // (Copilot round 3).
    setPageId('');
    // Reset the picked lead-form's display name too. Without this,
    // if the parent component keeps CreateMetaAdWizard mounted across
    // closes, the Review step can briefly show the previous run's
    // form name for a newly-selected id before LinkCreativeStep's
    // effect re-resolves (Copilot round 8 on PR #216).
    setLeadFormName(null);
  }, [isOpen, adSetIdProp, creativeSource]);

  const markDirty = () => setHasDirty(true);

  // Wrap setters so the cancel-confirm fires when there's unsaved work.
  const wrap =
    <T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
    (next: React.SetStateAction<T>) => {
      markDirty();
      setter(next);
    };

  // Step visibility — Ad Set picker is skipped when adSetId prop is set;
  // Creative step is skipped when the user picked an existing creative.
  const stepShouldRender = (step: number): boolean => {
    if (step === 2) return !adSetIdProp;
    if (step === 3) return creativeSource.mode === 'new';
    return true;
  };

  const validateStep = (step: number): boolean => {
    const next: FormErrors = {};

    if (step === 1) {
      if (!adName.trim()) {
        next.name = 'Ad name is required.';
      } else if (!NAME_PATTERN.test(adName)) {
        next.name = 'Name contains invalid control characters.';
      } else if (adName.length > 400) {
        next.name = 'Name must be 400 characters or fewer.';
      }
      // LEADS parents: pageId comes from either inheritance (when the
      // ad set's `promoted_object.page_id` is available) or the
      // dropdown fallback (when it isn't — see the isLeadsPendingAdSet
      // doc for the ingest-gap background). Two branches:
      //   - isLeadsPendingAdSet: still waiting for inheritance to
      //     resolve (query loading, or no ad set picked yet). Block
      //     with a "wait" message when a query is in flight (Copilot
      //     round 1 on PR #218 — otherwise the user could advance and
      //     submit with page_id="" before inheritance materializes).
      //     Defer silently when just waiting for the ad-set pick on
      //     step 2.
      //   - Otherwise: dropdown owns pageId (non-LEADS OR LEADS with
      //     no inheritance available). Standard pageId validation.
      if (isLeadsPendingAdSet) {
        if (isAdSetsPending) {
          next.pageId =
            'Loading Page from the ad set — try again in a moment.';
        }
        // else: !selectedAdSetId — user hasn't picked an ad set yet;
        // step 2 will collect it and pageId will resolve there.
      } else if (!pageId) {
        next.pageId = 'Pick a Facebook Page.';
      } else if (!/^\d+$/.test(pageId)) {
        next.pageId = 'Page ID must be numeric.';
      }
      if (instagramActorId && !/^\d+$/.test(instagramActorId)) {
        next.instagramActorId = 'Instagram actor ID must be numeric.';
      }
    }

    if (step === 2 && !adSetIdProp) {
      if (!selectedAdSetId) {
        next.adSetId = 'Pick an ad set.';
      } else if (isLeadsObjective) {
        // LEADS parent + picked ad set. Gate on isPending / isError so
        // the user isn't misled while the ad-sets query is still
        // resolving (Copilot round 1 + 2 on PR #218). If the query
        // resolved and there's no inheritance, DON'T block here —
        // Basics' dropdown fallback handles it (the ingest-gap
        // documented in `meta_campaigns.py:2311-2323` means null is
        // the common state for LEADS ad sets older than the wizard's
        // optimistic-write window, not a real "no Page attached").
        if (isAdSetsPending) {
          next.adSetId =
            'Loading ad set details — try again in a moment.';
        } else if (isAdSetsError) {
          next.adSetId =
            'Failed to load ad set details — check your connection and try again.';
        }
      }
    }

    if (step === 3 && creativeSource.mode === 'new') {
      if (!pendingImage) {
        next.image = 'Pick an image from your Media Library.';
      }
      if (!linkSpec.headline) {
        next.headline = 'Headline is required.';
      } else if (linkSpec.headline.length > LINK_CHAR_LIMITS.headline) {
        next.headline = `Headline must be ${LINK_CHAR_LIMITS.headline} characters or fewer.`;
      }
      if (!linkSpec.primary_text) {
        next.primary_text = 'Primary text is required.';
      } else if (linkSpec.primary_text.length > LINK_CHAR_LIMITS.primary_text) {
        next.primary_text = `Primary text must be ${LINK_CHAR_LIMITS.primary_text} characters or fewer.`;
      }
      if (
        linkSpec.description &&
        linkSpec.description.length > LINK_CHAR_LIMITS.description
      ) {
        next.description = `Description must be ${LINK_CHAR_LIMITS.description} characters or fewer.`;
      }
      if (!linkSpec.link_url) {
        next.link_url = 'Destination URL is required.';
      } else if (!/^https?:\/\//.test(linkSpec.link_url)) {
        next.link_url = 'Destination URL must start with http(s)://';
      }
      if (isLeadsFormPickerApplicable && !linkSpec.lead_gen_form_id) {
        next.lead_gen_form_id =
          'Pick a lead form — required for instant-form LEADS ad sets.';
      }
      // Block submission for non-ON_AD LEADS ad sets. The backend
      // router still enforces lead_gen_form_id for OUTCOME_LEADS parents
      // regardless of destination_type, so letting the wizard through
      // would surface as a confusing 422 pointing at a form the UI
      // never let the user pick. Amber info block in LinkCreativeStep
      // gives the "why"; this validation gives the "can't proceed".
      // Copilot round 3 on PR #216.
      if (
        isLeadsObjective &&
        !isLeadsFormPickerApplicable &&
        adSetDestinationType &&
        adSetDestinationType !== 'ON_AD'
      ) {
        next.lead_gen_form_id =
          `Flouri doesn't yet support LEADS ad sets with destination "${adSetDestinationType}" ` +
          'through the wizard. Create this ad in Meta Ads Manager directly, ' +
          'or pick an instant-form (ON_AD) ad set.';
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) return;
    let n = currentStep + 1;
    while (n < ALL_WIZARD_STEPS.length && !stepShouldRender(n)) {
      n += 1;
    }
    setCurrentStep(Math.min(n, ALL_WIZARD_STEPS.length));
  };

  const handlePrevious = () => {
    let p = currentStep - 1;
    while (p > 1 && !stepShouldRender(p)) {
      p -= 1;
    }
    setCurrentStep(Math.max(p, 1));
  };

  const handleStepClick = (step: number) => {
    if (step < currentStep && step >= 1 && stepShouldRender(step)) {
      setCurrentStep(step);
    }
  };

  const handleRequestClose = () => {
    if (hasDirty) {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  };

  const handleSubmit = async () => {
    // Re-run every visible step's validators.
    for (let step = 1; step <= ALL_WIZARD_STEPS.length - 1; step++) {
      if (!stepShouldRender(step)) continue;
      if (!validateStep(step)) {
        setCurrentStep(step);
        return;
      }
    }
    if (!adAccountId) {
      setSubmitError('Parent campaign is missing meta_ad_account_id.');
      return;
    }
    if (!selectedAdSetId) {
      setSubmitError('No ad set selected.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    // Materialize the pending image (new-mode only). Track the
    // materialized id so we can clean it up if the downstream Meta
    // create-ad call fails — same partial-failure invariant Google's
    // CreateDemandGenAdWizard maintains: a failed wizard submission
    // should not leave a Targeted MediaImage row orphaned in GCS.
    let materializedImageId: string | null = null;

    const cleanupMaterialized = async () => {
      if (!materializedImageId || !memberUuid) return;
      try {
        await batchDeleteImages({
          member_uuid: memberUuid,
          image_ids: [materializedImageId],
        });
      } catch (cleanupErr) {
        debug.log(
          'CreateMetaAdWizard: cleanup of materialized Targeted id failed',
          cleanupErr,
        );
      }
    };

    try {
      // 1. Materialize pendingImage in new mode (existing-mode submits
      //    use a creative_id, so there's no image to materialize).
      let imageUrlForBackend = '';
      if (creativeSource.mode === 'new') {
        if (!pendingImage) {
          // Should be caught by validateStep(3) above, but defense in
          // depth — the backend would 422 anyway.
          setSubmitError('Pick an image from your Media Library first.');
          setIsSubmitting(false);
          return;
        }
        const materialized = await materializePending([pendingImage]);
        if (materialized.length === 0) {
          setSubmitError('Image upload failed. Try picking again.');
          setIsSubmitting(false);
          return;
        }
        materializedImageId = materialized[0].id;
        imageUrlForBackend = materialized[0].signed_url;
      }

      // 2. Build the create-ad payload. For new mode, inject the
      //    materialized signed_url into the link spec's image_url —
      //    the backend uploads from this URL to /act_X/adimages.
      const payload: CreateMetaAdRequest = {
        meta_ad_account_id: adAccountId,
        ad_name: adName.trim(),
        status,
        page_id: pageId,
        creative:
          creativeSource.mode === 'existing'
            ? {
                mode: 'existing',
                creative_id: creativeSource.creative_id,
              }
            : {
                mode: 'new',
                spec: { ...linkSpec, image_url: imageUrlForBackend },
              },
        advantage_plus_creative: advantagePlusCreative,
      };
      if (instagramActorId) {
        payload.instagram_actor_id = instagramActorId;
      }

      const response = await metaAdsApi.createAd(selectedAdSetId, payload);
      debug.log('CreateMetaAdWizard: created ad', {
        campaign_id: campaignId,
        ad_set_id: selectedAdSetId,
        ad_id: response.ad_id,
        creative_id: response.creative_id,
      });

      // Optimistic app-config splice — mirrors CreateAdSetWizard. Uses the
      // server-authoritative `applied_entity` snapshot. See
      // docs/FRONTEND_MUTATION_PATTERNS.md for the "why".
      if (memberUuid && response.applied_entity) {
        // Backend passes through the raw dict; cast to local MetaAd at the
        // splice boundary.
        const appliedAd = response.applied_entity as unknown as import('@/types/metaCampaign').MetaAd;
        queryClient.setQueryData<AppConfiguration>(
          ['app-config', memberUuid],
          (prev) => appendOptimisticMetaAd(prev, selectedAdSetId, appliedAd),
        );
      }
      queryClient.invalidateQueries({ queryKey: ['campaign-from-cache', campaignId] });

      await onSuccess();
      onClose();
    } catch (err: unknown) {
      // Backend failed → clean up the Targeted MediaImage we
      // materialized so we don't leak GCS state. The cleanup is
      // best-effort; the user-facing error reflects the original
      // backend failure.
      await cleanupMaterialized();
      const apiError = err as {
        response?: {
          data?: { detail?: string | object; message?: string };
        };
        message?: string;
      };
      let message = 'Failed to create ad.';
      if (apiError?.response?.data) {
        const data = apiError.response.data;
        const detail = data.detail;
        if (typeof detail === 'string') {
          message = detail;
        } else if (detail) {
          message = JSON.stringify(detail);
        } else if (data.message) {
          message = data.message;
        }
      } else if (apiError?.message) {
        message = apiError.message;
      }
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const visibleSteps = ALL_WIZARD_STEPS.filter((_, idx) =>
    stepShouldRender(idx + 1),
  );
  const currentVisibleStep =
    ALL_WIZARD_STEPS.slice(0, currentStep).filter((_, idx) =>
      stepShouldRender(idx + 1),
    ).length;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Create new Meta ad"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-4 sm:my-8 relative">
        <button
          onClick={handleRequestClose}
          disabled={isSubmitting}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Close wizard"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 pr-8">
            Create New Ad
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Add a new ad to {parentCampaign.campaign_name}
          </p>
        </div>

        <div className="px-4 sm:px-6 py-2 border-b border-gray-100">
          <ProgressIndicator
            currentStep={currentVisibleStep}
            totalSteps={visibleSteps.length}
            steps={visibleSteps}
            onStepClick={(visibleIdx) => {
              let count = 0;
              for (let i = 1; i <= ALL_WIZARD_STEPS.length; i++) {
                if (stepShouldRender(i)) {
                  count += 1;
                  if (count === visibleIdx) {
                    handleStepClick(i);
                    return;
                  }
                }
              }
            }}
          />
        </div>

        <div
          ref={scrollBodyRef}
          className="px-6 py-6 overflow-y-auto"
          style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '400px' }}
        >
          {currentStep === 1 && (
            <AdBasicsStep
              adName={adName}
              status={status}
              pageId={pageId}
              instagramActorId={instagramActorId}
              availablePages={availablePages}
              isLoadingPages={isLoadingPages}
              isPageInherited={isPageInherited}
              isLeadsPendingAdSet={isLeadsPendingAdSet}
              onChangeName={(v) => {
                markDirty();
                setAdName(v);
              }}
              onChangeStatus={(v) => {
                markDirty();
                setStatus(v);
              }}
              onChangePageId={(v) => {
                markDirty();
                setPageId(v);
              }}
              onChangeInstagramActorId={(v) => {
                markDirty();
                setInstagramActorId(v);
              }}
              errors={{
                name: errors.name,
                pageId: errors.pageId,
                instagramActorId: errors.instagramActorId,
              }}
            />
          )}

          {currentStep === 2 && stepShouldRender(2) && (
            <AdSetPickerStep
              parentCampaign={parentCampaign}
              selectedAdSetId={selectedAdSetId}
              onChangeAdSetId={(v) => {
                markDirty();
                setSelectedAdSetId(v);
              }}
              errors={{ adSetId: errors.adSetId }}
            />
          )}

          {currentStep === 3 && creativeSource.mode === 'new' && (
            <LinkCreativeStep
              value={linkSpec}
              onChange={wrap(setLinkSpec)}
              pendingImage={pendingImage}
              onChangePendingImage={(next) => {
                markDirty();
                setPendingImage(next);
              }}
              advantagePlusCreative={advantagePlusCreative}
              onChangeAdvantagePlusCreative={wrap(setAdvantagePlusCreative)}
              isLeadsFormPickerApplicable={isLeadsFormPickerApplicable}
              parentDestinationType={adSetDestinationType}
              pageId={pageId}
              adAccountId={adAccountId}
              onLeadFormNameChange={setLeadFormName}
              errors={{
                image: errors.image,
                headline: errors.headline,
                primary_text: errors.primary_text,
                description: errors.description,
                link_url: errors.link_url,
                lead_gen_form_id: errors.lead_gen_form_id,
              }}
            />
          )}

          {currentStep === 4 && (
            <AdReviewStep
              adName={adName}
              status={status}
              adSetId={selectedAdSetId}
              pageId={pageId}
              instagramActorId={instagramActorId}
              creativeSource={
                creativeSource.mode === 'new'
                  ? { mode: 'new', spec: linkSpec }
                  : creativeSource
              }
              pickedCreative={pickedCreative}
              pendingImage={pendingImage}
              advantagePlusCreative={advantagePlusCreative}
              onChangeAdvantagePlusCreative={wrap(setAdvantagePlusCreative)}
              leadFormName={leadFormName}
              onEditBasics={() => setCurrentStep(1)}
              onEditCreative={
                creativeSource.mode === 'new'
                  ? () => setCurrentStep(3)
                  : undefined
              }
            />
          )}
        </div>

        <div className="hidden sm:block px-6 py-4 border-t border-gray-200">
          {submitError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p role="alert" className="text-sm text-red-800">
                {linkifyUrls(submitError)}
              </p>
            </div>
          )}
          <div className="flex justify-between items-center">
            <Button
              onClick={handlePrevious}
              disabled={isSubmitting || currentStep <= 1}
              variant="secondary"
            >
              Previous
            </Button>
            <div className="text-sm text-gray-500">
              Step {currentVisibleStep} of {visibleSteps.length}
            </div>
            {currentStep < ALL_WIZARD_STEPS.length ? (
              <Button onClick={handleNext} disabled={isSubmitting}>
                Next
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                variant="primary"
              >
                {isSubmitting ? 'Creating Ad...' : 'Create Ad'}
              </Button>
            )}
          </div>
        </div>

        <div className="block sm:hidden px-6 py-4 border-t border-gray-200">
          {submitError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p role="alert" className="text-sm text-red-800">
                {linkifyUrls(submitError)}
              </p>
            </div>
          )}
          <div className="flex flex-col items-center gap-3">
            {currentStep < ALL_WIZARD_STEPS.length ? (
              <Button
                onClick={handleNext}
                disabled={isSubmitting}
                className="w-full"
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                variant="primary"
                className="w-full"
              >
                {isSubmitting ? 'Creating Ad...' : 'Create Ad'}
              </Button>
            )}
            <div className="text-sm text-gray-500">
              Step {currentVisibleStep} of {visibleSteps.length}
            </div>
            <Button
              onClick={handlePrevious}
              disabled={isSubmitting || currentStep <= 1}
              variant="secondary"
              className="w-full"
            >
              Previous
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => {
          setShowCancelConfirm(false);
          onClose();
        }}
        title="Discard ad?"
        message="You have unsaved changes. Closing now will discard the ad you've been setting up."
        confirmText="Discard"
        cancelText="Keep editing"
        variant="warning"
      />
    </div>
  );
};

